import { useState } from 'react'
import Swal from 'sweetalert2'
import { DELETE, GET, POST, PUT } from '../../../../../services/api'
import { VariationType, VariationResponseType, LocationType, SelectType, VariationFilters } from '../types'
import { mapVariationResponse } from './variationMapper'

type TableParams = {
    page?: number
    pageSize?: number
    search?: string
    sorting?: { field: string; direction: 'asc' | 'desc' }
    filters?: VariationFilters
}

export function useVariations() {
    const [variations, setVariations] = useState<VariationType[]>([])
    const [variationId, setVariationId] = useState<number | null>(null)
    const [locationId, setLocationId] = useState(-1)
    const [locations, setLocations] = useState<SelectType[]>([])
    const [loading, setLoading] = useState(false)
    const [tableLoading, setTableLoading] = useState(false) 

    const [pagination, setPagination] = useState({
        page: 1,
        pageSize: 25,
        total: 0,
        pageSizes: [10, 25, 50, 100],
    })
    const [sorting, setSorting] = useState({
        field: 'id',
        direction: 'asc' as 'asc' | 'desc',
    })
    const [searchTerm, setSearchTerm] = useState('')
    const [filters, setFilters] = useState<VariationFilters>({})

    const getVariations = async (locationIdSend: number, params?: TableParams) => {
        try {
            setTableLoading(true)
            const currentPage = params?.page || pagination.page
            const currentPageSize = params?.pageSize || pagination.pageSize
            const currentSearch = params?.search || searchTerm
            const currentSorting = params?.sorting || sorting

            const query = `?field=${currentSorting.field}&order=${currentSorting.direction.toUpperCase()}&page=${currentPage - 1}&take=${currentPageSize}&search=${currentSearch}`


            const currentFilters = params?.filters ?? filters

            const res = await POST(`/variations/filter-admin${query}`, {
                haveImage: currentFilters.haveImage,
                havePrice: currentFilters.havePrice,
                productStatus: currentFilters.productStatus,
                variationStatus: currentFilters.variationStatus,
                haveInventory: currentFilters.haveInventory,
                classificationId: currentFilters.classificationId,
                havePromotionPrice: currentFilters.havePromotionPrice,
                locationId: locationIdSend || -1,
            })
            setVariations(res.data.map((row: VariationResponseType) => mapVariationResponse(row)))

            if (res.meta) {
                const paginagionInfo = {
                    total: res.meta.itemCount,
                    page: currentPage,
                    pageSize: currentPageSize,
                }
                setPagination((prev) => ({
                    ...prev,
                    ...paginagionInfo,
                }))
            }
        } catch (e) {
            setLoading(false)
            console.log(e)
        }finally {
            setTableLoading(false) 
        }
    }

    const deleteVariation = async () => {
        try {
            setLoading(true)
            const res = await DELETE(`/variations/${variationId}`)
            const isSuccess = res.success === true
            Swal.fire({
                icon: isSuccess ? 'success' : 'error',
                title: isSuccess ? 'Variação deletada' : 'Erro',
                text: isSuccess ? 'Deleção realizada com sucesso!' : 'Erro ao deletar a variação!',
            }).then(async () => await getVariations(locationId))
            setLoading(false)
        } catch (e) {
            setLoading(false)
            Swal.fire({
                icon: 'error',
                title: 'Erro',
                text: 'Erro ao deletar a variação!',
            }).then(async () => await getVariations(locationId))
            console.log(e)
        }
    }

    const changeVariation = async (item: any, valueChange: string) => {
        try {
            const res = await PUT(`/variations/${item.id}`, { [valueChange]: item[valueChange] })
            if (res.success) {
                setVariations((prev) =>
                    prev.map((v) => (v.id === item.id ? { ...v, [valueChange]: item[valueChange] } : v))
                )
            }
        } catch (e) {
            console.log(e)
        }
    }

    const getLocations = async () => {
        try {
            const res = await GET(`/location`)
            const allLocations: LocationType[] = [
                { id: -1, name: 'Todas', type: '', status: true },
                ...res.data.filter((loc: LocationType) => loc.type !== 'address'),
            ]
            setLocations(allLocations.map((loc) => ({ value: loc.id, select: loc.name })))

            const locationActive =
                allLocations.find((loc) => loc.status && loc.code === 'withdraw') ||
                allLocations.find((loc) => loc.status && loc.id > 0)

            if (locationActive) {
                setLocationId(locationActive.id)
                await getVariations(locationActive.id)
            }
        } catch (e) {
            setLoading(false)
            console.log(e)
        }
    }

    const importFile = async (
        file: File | null,
        clearFile: () => void
    ) => {
        try {
            if (!file) {
                Swal.fire({ icon: 'error', title: 'Campos faltantes', text: 'Verifique se foi selecionado um arquivo!' })
                return
            }
            setLoading(true)
            const formData = new FormData()
            formData.append('file', file)

            const res = await POST('/product/import_minimum_stock', formData)
            setLoading(false)

            const isSuccess = res.success === true
            Swal.fire({
                icon: isSuccess ? 'success' : 'error',
                title: isSuccess ? 'Sucesso!' : 'Erro',
                text: isSuccess ? 'Importação realizada com sucesso!' : 'Erro ao importar arquivo!',
            }).then(async () => {
                await getLocations()
                await getVariations(locationId)
            })
        } catch (e: any) {
            setLoading(false)
            console.log(e)
            Swal.fire({ icon: 'error', title: 'Erro', text: e }).then(async () => {
                await getLocations()
                await getVariations(locationId)
            })
        }
        clearFile()
    }

    const handleFilterChange = async (newFilters: VariationFilters) => {
        setFilters(newFilters)
    }

    const handleDataChange = async (params: {
        page: number
        pageSize: number
        search: string
        sorting?: { field: string; direction: 'asc' | 'desc' }
        filters?: VariationFilters
    }) => {
        setPagination((prev) => ({ ...prev, page: params.page, pageSize: params.pageSize }))
        if (params.sorting) setSorting(params.sorting)
        setSearchTerm(params.search)
        await getVariations(locationId, params)
    }

    const handlePaginationChange = (newPagination: any) => {
        setPagination(newPagination)
        getVariations(locationId, {
            page: newPagination.page,
            pageSize: newPagination.pageSize,
            search: searchTerm,
            sorting: sorting.field ? sorting : undefined,
        })
    }

    const handleSortingChange = (newSorting: any) => {
        setSorting(newSorting)
        getVariations(locationId, {
            page: pagination.page,
            pageSize: pagination.pageSize,
            search: searchTerm,
            sorting: newSorting,
        })
    }

    const init = async (checkStockIntegration: () => void) => {
        setLoading(true)
        await getLocations()
        checkStockIntegration()
        setLoading(false)
    }

    return {
        variations,
        variationId,
        setVariationId,
        locationId,
        setLocationId,
        locations,
        loading,
        setLoading,
        pagination,
        sorting,
        searchTerm,
        getVariations,
        deleteVariation,
        changeVariation,
        importFile,
        handleDataChange,
        handlePaginationChange,
        handleSortingChange,
        handleFilterChange,
        filters,
        init,
        tableLoading,
        setVariations
    }
}
